import { useState } from "react";
import type { HomeResponse } from "../shared/dto";
import { WorkingAvatar } from "./WorkingAvatar";
import { actionIssueFrom, actionIssueFromThrown, actionIssueLocaleKey, type ActionIssue } from "./action-error";
import "./partner-assigning.css";
import "./partner-message.css";

/** The app-level `t` takes a key only; the composer fills its one variable here. */
function fill(text: string, variables: Record<string, string>): string {
  return Object.entries(variables).reduce((value, [name, replacement]) => value.replaceAll(`{${name}}`, replacement), text);
}

export function PartnerControl({ partner, t, compact = false, showPhoto = true, csrfToken, replyEmail }: {
  partner: HomeResponse["partner"];
  t: (key: string) => string;
  compact?: boolean;
  showPhoto?: boolean;
  /**
   * F162: with a token the card carries the in-app composer (the message goes
   * through the server to the partner); without one it falls back to the
   * plain links, which is what the static previews and tests render.
   */
  csrfToken?: string;
  /** The address the partner will answer to; shown after a successful send. */
  replyEmail?: string | null;
}) {
  const [photoBroken, setPhotoBroken] = useState(false);
  // F139: a client may legitimately have no partner yet, so this is a WAITING
  // state with an owner, not an empty card. It wears the one working marker
  // the product has (F53 WorkingAvatar, size "card" beside a heading) - never
  // a new shape - and the copy says who is working, how long it usually takes
  // and what the client will be able to do the moment it lands.
  if (!partner) {
    return (
      <section className={`partner-card partner-assigning${compact ? " partner-compact" : ""}`}>
        <div className="partner-assigning-head">
          <WorkingAvatar size="card" />
          <strong>{t("partner.assigningTitle")}</strong>
        </div>
        <p>{t("partner.assigningCopy")}</p>
      </section>
    );
  }
  const name = partner.displayName ?? t("partner.fallbackName");
  const initials = name.split(/\s+/).map((part) => part[0] ?? "").join("").slice(0, 2).toUpperCase();
  // Incomplete staff profile: missing photo falls back to initials, a missing
  // booking link hides only the button - the partner card itself always shows.
  return (
    <section id="partner-card" className={`partner-card ${compact ? "partner-compact" : ""}${showPhoto ? "" : " partner-reviewed-portrait"}`}>
      <div className="partner-head">
        {!showPhoto || photoBroken || !partner.photoUrl ? (
          <span className="partner-initials" aria-hidden="true">{initials}</span>
        ) : (
          <img src={partner.photoUrl} alt="" onError={() => setPhotoBroken(true)} />
        )}
        <div><strong>{name}</strong><small>{t("partner.role")}</small></div>
      </div>
      {/* F146: the card says what a PARTNER is for, never what this partner
          wrote about themselves. The stored bio was a free-text field the
          partner filled in their own language, so an English client met a
          Czech paragraph on the one card that is supposed to tell them who is
          holding their hand. The sentence is fixed in both locales, always
          renders (no partner can leave it empty), and only the identity around
          it - name, photo, contact, booking - still comes from the profile. */}
      <p>{t("partner.roleCopy")}</p>
      {csrfToken ? (
        <>
          <p className="partner-help-copy">{t("partner.contactCopy")}</p>
          <PartnerMessage csrfToken={csrfToken} t={t} replyEmail={replyEmail ?? null} publicEmail={partner.publicEmail} />
          {(partner.calendlyUrl || partner.telegramUrl) && (
            <div className="partner-actions">
              {partner.telegramUrl && (
                <a className="secondary-button partner-link" href={partner.telegramUrl} target="_blank" rel="noreferrer">{t("partner.telegram")}</a>
              )}
              {partner.calendlyUrl && (
                <a className="secondary-button partner-link" href={partner.calendlyUrl} target="_blank" rel="noreferrer">{t("partner.book")}</a>
              )}
            </div>
          )}
        </>
      ) : (partner.publicEmail || partner.calendlyUrl || partner.telegramUrl) && (
        <>
          <p className="partner-help-copy">{t(partner.publicEmail ? "partner.contactCopy" : "partner.bookingCopy")}</p>
          <div className="partner-actions">
            {partner.publicEmail && (
              <a className="secondary-button partner-link" href={`mailto:${partner.publicEmail}`}>{t("partner.write")}</a>
            )}
            {partner.telegramUrl && (
              <a className="secondary-button partner-link" href={partner.telegramUrl} target="_blank" rel="noreferrer">{t("partner.telegram")}</a>
            )}
            {partner.calendlyUrl && (
              <a className="secondary-button partner-link" href={partner.calendlyUrl} target="_blank" rel="noreferrer">{t("partner.book")}</a>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * F162 (Tomas Pohl 2026-09-22): the "write to your partner" button was a
 * `mailto:` link, which does nothing on a machine with no mail app set up (a
 * browser-only Gmail user, a work laptop). JJ Fox Coaching had to answer a
 * notification mail to reach anyone. The message now goes through the
 * product: POST /api/partner/message mails it to the partner with the client's
 * address to reply to, and the card says where the answer will land. The
 * mailto and the bare address stay underneath as a quiet second way out.
 */
function PartnerMessage(props: {
  csrfToken: string;
  t: (key: string) => string;
  replyEmail: string | null;
  publicEmail: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [failed, setFailed] = useState<ActionIssue | null>(null);
  const { t } = props;

  async function send(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setFailed(null);
    try {
      const response = await fetch("/api/partner/message", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken, "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ text: trimmed })
      });
      if (!response.ok) {
        setFailed(await actionIssueFrom(response));
        return;
      }
      const body = await response.json().catch(() => null) as { ok?: boolean; replyEmail?: string } | null;
      if (body?.ok !== true) {
        setFailed(actionIssueFromThrown());
        return;
      }
      setSent(body.replyEmail ?? props.replyEmail ?? "");
      setText("");
      setOpen(false);
    } catch {
      setFailed(actionIssueFromThrown());
    } finally {
      setSending(false);
    }
  }

  const fallback = props.publicEmail ? (
    <p className="partner-message-fallback">
      <a href={`mailto:${props.publicEmail}`}>{t("partner.messageMailApp")}</a>
      {" · "}
      {fill(t("partner.messageAddress"), { email: props.publicEmail })}
    </p>
  ) : null;

  if (sent !== null) {
    return (
      <div className="partner-message-sent">
        <p className="message success-message" role="status">
          <strong>{t("partner.messageSentTitle")}</strong>
          {fill(t("partner.messageSent"), { email: sent })}
        </p>
        <div className="partner-actions">
          <button className="secondary-button partner-link" type="button" onClick={() => setSent(null)}>{t("partner.messageAnother")}</button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <>
        <div className="partner-actions">
          <button className="secondary-button partner-link" type="button" onClick={() => setOpen(true)}>{t("partner.write")}</button>
        </div>
        {fallback}
      </>
    );
  }

  return (
    <form className="partner-message" onSubmit={(event) => void send(event)}>
      <label htmlFor="partner-message-text">{t("partner.messageLabel")}</label>
      <textarea
        id="partner-message-text"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={t("partner.messagePlaceholder")}
        rows={4}
        maxLength={2000}
        autoFocus
      />
      {failed && <p className="message error-message" role="alert">{t(actionIssueLocaleKey(failed))}</p>}
      <div className="partner-message-actions">
        <button className="secondary-button" type="submit" disabled={sending || !text.trim()}>
          {sending ? t("partner.messageSending") : t("partner.messageSend")}
        </button>
        <button className="quiet-link" type="button" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
      </div>
      {fallback}
    </form>
  );
}
